'use client';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, Typography, useTheme } from '@mui/material';
import Link from 'next/link';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { ComponentBlocksActionBlock, Enum_Componentblocksactionblock_Asideposition } from '@/types';

type Position = Enum_Componentblocksactionblock_Asideposition;

function flexDirectionForPosition(position: Position | null | undefined) {
  switch (position) {
    case 'Left':
      return 'row-reverse';
    case 'Right':
      return 'row';
    case 'None':
    default:
      return null;
  }
}

export function ActionBlock({ block }: { block: ComponentBlocksActionBlock }) {
  const { ButtonText, ButtonLink, ButtonArrow, CTAIntro, CTAFooter, AsideContent, AsidePosition } =
    block;
  const theme = useTheme();

  const flexDirection = flexDirectionForPosition(AsidePosition);
  const showAside = flexDirection !== null && AsideContent;
  const isRow = flexDirection === 'row' || flexDirection === 'row-reverse';

  return (
    <CustomBackground
      backgroundOptions={(block.ActionBlockBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        (block.ActionBlockDisplayOptions ?? {
          PaddingTop: 'md',
          PaddingBottom: 'md',
          PaddingInline: 'md',
        }) as StrapiDisplayOptions
      }
      boxProps={{ 'data-component': 'ActionBlock' } as any}
    >
      <Box
        sx={{
          display: { xs: 'flex', sm: showAside ? 'grid' : 'flex' },
          flexDirection: { xs: 'column', sm: flexDirection ?? 'row' },
          gridTemplateColumns: '1fr 1fr',
          alignItems: 'center',
          gap: { xs: 1, sm: 6 },
          justifyContent: 'space-around',
          mx: 'auto',
        }}
      >
        {showAside && (
          <Box sx={{ flex: isRow ? '1 1 0%' : undefined }}>
            <GlobalBlockRenderer content={AsideContent} />
          </Box>
        )}

        {ButtonText && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            {CTAIntro && (
              <Box sx={{ mx: 'auto' }}>
                <GlobalBlockRenderer content={CTAIntro} />
              </Box>
            )}
            {ButtonLink ? (
              ButtonLink.startsWith('http') ? (
                <Button
                  variant="contained"
                  color="secondary"
                  href={ButtonLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={ButtonArrow ? <ChevronRightIcon /> : undefined}
                  sx={{
                    '.MuiButton-endIcon *': {
                      fontSize: '1.5em',
                    },
                  }}
                >
                  {ButtonText}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="secondary"
                  component={Link}
                  href={ButtonLink}
                  endIcon={ButtonArrow ? <ChevronRightIcon /> : undefined}
                >
                  {ButtonText}
                </Button>
              )
            ) : (
              <Button
                variant="contained"
                color="secondary"
                endIcon={ButtonArrow ? <ChevronRightIcon /> : undefined}
              >
                {ButtonText}
              </Button>
            )}
            {CTAFooter && (
              <Typography variant="tiny" align="center" sx={{ color: theme.palette.purple[700] }}>
                {CTAFooter}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </CustomBackground>
  );
}
